import type { Maybe } from '@adornis/base/utilTypes.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html, nothing } from 'lit';
import { LakAPI } from '../api/LakAPI.js';

export interface LAKAdjustmentDialogValue {
  _id?: Maybe<string>;
  name: Maybe<string>;
  start: Maybe<Date>;
  end: Maybe<Date>;
  hoursPerWeek: Maybe<number>;
  hours: Maybe<number>;
}

const formatHours = (hours: Maybe<number>) =>
  (hours ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const LAKAdjustmentDialog = stateful(
  (props: {
    userID: string;
    creditStart: Maybe<Date>;
    adjustment: Maybe<LAKAdjustmentDialogValue>;
    resolve: (value: LAKAdjustmentDialogValue) => void;
    reject: () => void;
  }) => {
    const initial = props.adjustment;
    const [name, setName] = useState(initial?.name);
    const [oneTime, setOneTime] = useState(!!initial && !initial.end);
    const [start, setStart] = useState(initial?.start);
    const [end, setEnd] = useState(initial?.end);
    const [hoursPerWeek, setHoursPerWeek] = useState(initial?.hoursPerWeek);
    const [hours, setHours] = useState(initial?.hours);

    /**
     * The weeks are counted on the server, by the same function the calculation uses, so what the dialog promises
     * is what the overview will show.
     */
    const hasPeriod = !oneTime && !!start && !!end && end >= start;
    const { value: preview, loading: previewLoading } = useQuery(
      () =>
        hasPeriod
          ? LakAPI.previewAdjustmentWeeks(
              props.userID,
              start,
              end,
            )({
              _class: 1,
              contractID: 1,
              supplementID: 1,
              adjustmentID: 1,
              start: 1,
              creditedWeeks: 1,
              projectedCreditedWeeks: 1,
              years: {
                _class: 1,
                contractID: 1,
                supplementID: 1,
                adjustmentID: 1,
                year: 1,
                creditedWeeks: 1,
                projectedCreditedWeeks: 1,
              },
            })
          : Promise.resolve(null),
      [hasPeriod, start?.getTime(), end?.getTime()],
    );
    const weeks = hasPeriod ? (preview?.projectedCreditedWeeks ?? preview?.creditedWeeks ?? 0) : 0;
    const yearWeeks = (preview?.years ?? [])
      .map(year => `${year?.year}: ${year?.projectedCreditedWeeks ?? year?.creditedWeeks ?? 0}`)
      .join(', ');

    const beforeCreditStart = !!start && !!props.creditStart && start < props.creditStart;
    const canSave =
      !!name?.trim() && !!start && !beforeCreditStart && (oneTime ? !!hours : hasPeriod && weeks > 0 && !!hoursPerWeek);

    const kindButton = (label: string, value: boolean) => html`
      <button class="btn btn-sm join-item ${oneTime === value ? 'btn-active' : ''}" @click=${() => setOneTime(value)}>
        ${label}
      </button>
    `;

    return html`
      <div class="dialog-container">
        ${DialogHeader({ title: initial?._id ? 'Abstimmung ändern' : 'Manuelle Abstimmung eintragen', close: props.reject, closeLabel: 'Dialog schließen' })}
        <p>
          Läuft neben den Verträgen und zählt zum Stand dazu oder zieht ab - etwa Stunden, die während der Elternzeit
          entnommen werden, oder Stunden, die ausgezahlt werden.
        </p>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Bezeichnung</span>
          ${TextInput({
            value: name,
            onPicked: value => setName(value ?? undefined),
            placeholder: 'z. B. Elternzeit mit 5 Std. Teilzeit',
          })}
        </div>

        <div class="join">${kindButton('Pro Woche', false)} ${kindButton('Einmalig', true)}</div>

        ${
          oneTime
            ? html`
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-semibold text-base-content/70">Datum</span>
                  ${DateInput({ value: start, onPicked: value => setStart(value ?? undefined) })}
                </div>
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-semibold text-base-content/70">Stunden</span>
                  ${NumberInput({
                    value: hours,
                    onPicked: value => setHours(value ?? undefined),
                    placeholder: 'z. B. -40 für eine Auszahlung',
                    suffix: 'Std.',
                  })}
                  <p class="text-sm text-base-content/70">
                    Negativ zum Abziehen. Zählt zu dem Kalenderjahr, in dem das Datum liegt.
                  </p>
                </div>
              `
            : html`
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-semibold text-base-content/70">Zeitraum</span>
                  <div class="flex items-center gap-2">
                    ${DateInput({ value: start, onPicked: value => setStart(value ?? undefined) })} –
                    ${DateInput({ value: end, onPicked: value => setEnd(value ?? undefined) })}
                  </div>
                  ${
                    hasPeriod
                      ? html`
                          <p class="text-sm">
                            ${
                              previewLoading
                                ? 'Wochen werden gezählt …'
                                : html`<span class="font-semibold">${weeks} volle Wochen</span>${
                                      yearWeeks ? ` (${yearWeeks})` : nothing
                                    }`
                            }
                          </p>
                        `
                      : nothing
                  }
                  <p class="text-sm text-base-content/70">
                    Gezählt wird wie bei den Verträgen: nur volle Wochen von Montag bis Sonntag, eine Woche zählt zu dem
                    Jahr, in dem ihr Donnerstag liegt. Abwesenheiten werden hier nicht abgezogen.
                  </p>
                </div>
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-semibold text-base-content/70">Pro Woche</span>
                  ${NumberInput({
                    value: hoursPerWeek,
                    onPicked: value => setHoursPerWeek(value ?? undefined),
                    placeholder: 'z. B. -1 für eine entnommene Stunde',
                    suffix: 'Std.',
                  })}
                  ${
                    hasPeriod && hoursPerWeek
                      ? html`
                          <p class="text-sm">
                            ${weeks} Wochen × ${formatHours(hoursPerWeek)} Std. =
                            <span class="font-semibold ${hoursPerWeek < 0 ? 'text-error' : 'text-success'}">
                              ${formatHours(Math.round(weeks * hoursPerWeek * 100) / 100)} Std.
                            </span>
                          </p>
                        `
                      : nothing
                  }
                </div>
              `
        }
        ${
          beforeCreditStart && props.creditStart
            ? html`
                <p class="text-sm text-error">
                  Liegt vor dem ${formatCalendarDay(props.creditStart, 'dd.MM.yyyy')}. Alles davor steckt im Übertrag.
                </p>
              `
            : nothing
        }

        <button
          class="btn btn-primary"
          ?disabled=${!canSave}
          @click=${() =>
            props.resolve({
              _id: initial?._id,
              name,
              start,
              end: oneTime ? undefined : end,
              hoursPerWeek: oneTime ? undefined : hoursPerWeek,
              hours: oneTime ? hours : undefined,
            })}
        >
          Speichern
        </button>
      </div>
    `;
  },
);

export async function openLAKAdjustmentDialog(params: {
  userID: string;
  creditStart: Maybe<Date>;
  adjustment?: LAKAdjustmentDialogValue;
}) {
  return showDialog<LAKAdjustmentDialogValue>({
    content: (resolve, reject) => LAKAdjustmentDialog({ ...params, adjustment: params.adjustment, resolve, reject }),
  });
}
